import React from 'react';
import { motion } from 'framer-motion';
import { OnboardingV2State } from '../../../store/onboardingV2/types.ts';
import { Check, User, Building2, Phone, Globe, FileText, HardHat, MapPin, Users, X } from 'lucide-react';
import styles from './ConversationalOnboarding.module.scss';

interface ProfilePreviewProps {
  state: OnboardingV2State;
  onRemoveSkill: (skillId: number) => void;
  onRemoveZone: (zoneId: number | string) => void;
  onRemoveProfileCategory: (categoryId: number) => void;
}

const ProfilePreview: React.FC<ProfilePreviewProps> = ({ state, onRemoveSkill, onRemoveZone, onRemoveProfileCategory }) => {
  const personaForm = state.personaForm;
  const empresaForm = state.empresaForm;
  const personaBio = typeof personaForm.bio === 'string' ? personaForm.bio.trim() : '';
  const empresaBio = typeof empresaForm.bio === 'string' ? empresaForm.bio.trim() : '';
  const hasData = state.accountType !== null;

  if (!hasData) {
    return (
      <div className={styles.profilePreview}>
        <div className={styles.profilePreviewEmpty}>
          <p className={styles.profilePreviewEmptyText}>
            Tu perfil se irá completando aquí mientras avanzás
          </p>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      className={styles.profilePreview}
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className={styles.profilePreviewHeader}>
        <h2 className={styles.profilePreviewTitle}>Tu perfil</h2>
        <div className={styles.profilePreviewBadge}>
          {state.accountType === 'PERSON' ? (
            <>
              <User size={14} />
              <span>Profesional</span>
            </>
          ) : (
            <>
              <Building2 size={14} />
              <span>Empresa</span>
            </>
          )}
        </div>
      </div>

      <div className={styles.profilePreviewContent}>
        {state.accountType === 'PERSON' ? (
          <>
            {(personaForm.name || personaForm.lastName) && (
              <div className={styles.profilePreviewItem}>
                <div className={styles.profilePreviewItemHeader}>
                  <User size={16} className={styles.profilePreviewIcon} />
                  <span className={styles.profilePreviewLabel}>Nombre</span>
                  <Check size={14} className={styles.profilePreviewCheck} />
                </div>
                <p className={styles.profilePreviewValue}>
                  {personaForm.name} {personaForm.lastName}
                </p>
              </div>
            )}

            {personaForm.tel && (
              <div className={styles.profilePreviewItem}>
                <div className={styles.profilePreviewItemHeader}>
                  <Phone size={16} className={styles.profilePreviewIcon} />
                  <span className={styles.profilePreviewLabel}>Teléfono</span>
                  <Check size={14} className={styles.profilePreviewCheck} />
                </div>
                <p className={styles.profilePreviewValue}>{personaForm.tel}</p>
              </div>
            )}

            {personaForm.webSite && (
              <div className={styles.profilePreviewItem}>
                <div className={styles.profilePreviewItemHeader}>
                  <Globe size={16} className={styles.profilePreviewIcon} />
                  <span className={styles.profilePreviewLabel}>Sitio web</span>
                  <Check size={14} className={styles.profilePreviewCheck} />
                </div>
                <p className={styles.profilePreviewValue}>{personaForm.webSite}</p>
              </div>
            )}

            {personaBio && (
              <div className={styles.profilePreviewItem}>
                <div className={styles.profilePreviewItemHeader}>
                  <FileText size={16} className={styles.profilePreviewIcon} />
                  <span className={styles.profilePreviewLabel}>Sobre mí</span>
                  <Check size={14} className={styles.profilePreviewCheck} />
                </div>
                <p className={styles.profilePreviewValue}>{personaBio}</p>
              </div>
            )}
          </>
        ) : (
          <>
            {empresaForm.companyName && (
              <div className={styles.profilePreviewItem}>
                <div className={styles.profilePreviewItemHeader}>
                  <Building2 size={16} className={styles.profilePreviewIcon} />
                  <span className={styles.profilePreviewLabel}>Empresa</span>
                  <Check size={14} className={styles.profilePreviewCheck} />
                </div>
                <p className={styles.profilePreviewValue}>{empresaForm.companyName}</p>
              </div>
            )}

            {empresaForm.tel && (
              <div className={styles.profilePreviewItem}>
                <div className={styles.profilePreviewItemHeader}>
                  <Phone size={16} className={styles.profilePreviewIcon} />
                  <span className={styles.profilePreviewLabel}>Teléfono</span>
                  <Check size={14} className={styles.profilePreviewCheck} />
                </div>
                <p className={styles.profilePreviewValue}>{empresaForm.tel}</p>
              </div>
            )}

            {empresaForm.webSite && (
              <div className={styles.profilePreviewItem}>
                <div className={styles.profilePreviewItemHeader}>
                  <Globe size={16} className={styles.profilePreviewIcon} />
                  <span className={styles.profilePreviewLabel}>Sitio web</span>
                  <Check size={14} className={styles.profilePreviewCheck} />
                </div>
                <p className={styles.profilePreviewValue}>{empresaForm.webSite}</p>
              </div>
            )}

            {empresaBio && (
              <div className={styles.profilePreviewItem}>
                <div className={styles.profilePreviewItemHeader}>
                  <FileText size={16} className={styles.profilePreviewIcon} />
                  <span className={styles.profilePreviewLabel}>Sobre mi empresa</span>
                  <Check size={14} className={styles.profilePreviewCheck} />
                </div>
                <p className={styles.profilePreviewValue}>{empresaBio}</p>
              </div>
            )}
          </>
        )}

        {state.userSkills.length > 0 && (
          <div className={styles.profilePreviewItem}>
            <div className={styles.profilePreviewItemHeader}>
              <HardHat size={16} className={styles.profilePreviewIcon} />
              <span className={styles.profilePreviewLabel}>Habilidades</span>
              <Check size={14} className={styles.profilePreviewCheck} />
            </div>
            <div className={styles.profilePreviewSkills}>
              {state.userSkills.slice(0, 5).map((skill) => (
                <span key={skill.id} className={styles.profilePreviewSkillChip}>
                  {skill.name}
                  <button
                    type="button"
                    className={styles.profilePreviewSkillRemove}
                    onClick={() => onRemoveSkill(skill.id)}
                    aria-label={`Eliminar ${skill.name}`}
                  >
                    <X size={12} />
                  </button>
                </span>
              ))}
              {state.userSkills.length > 5 && (
                <span className={styles.profilePreviewSkillMore}>
                  +{state.userSkills.length - 5} más
                </span>
              )}
            </div>
          </div>
        )}

        {state.preferredProfileCategories.length > 0 && (
          <div className={styles.profilePreviewItem}>
            <div className={styles.profilePreviewItemHeader}>
              <Users size={16} className={styles.profilePreviewIcon} />
              <span className={styles.profilePreviewLabel}>Con quién conectar</span>
              <Check size={14} className={styles.profilePreviewCheck} />
            </div>
            <div className={styles.profilePreviewSkills}>
              {state.preferredProfileCategories.slice(0, 5).map((categoryId) => {
                const categoryName = state.skillCategories.find((category) => category.id === categoryId)?.name;
                if (!categoryName) return null;
                return (
                  <span key={categoryId} className={styles.profilePreviewSkillChip}>
                    {categoryName}
                    <button
                      type="button"
                      className={styles.profilePreviewSkillRemove}
                      onClick={() => onRemoveProfileCategory(categoryId)}
                      aria-label={`Eliminar ${categoryName}`}
                    >
                      <X size={12} />
                    </button>
                  </span>
                );
              })}
              {state.preferredProfileCategories.length > 5 && (
                <span className={styles.profilePreviewSkillMore}>
                  +{state.preferredProfileCategories.length - 5} más
                </span>
              )}
            </div>
          </div>
        )}

        {(state.allCountry || state.workZones.length > 0) && (
          <div className={styles.profilePreviewItem}>
            <div className={styles.profilePreviewItemHeader}>
              <MapPin size={16} className={styles.profilePreviewIcon} />
              <span className={styles.profilePreviewLabel}>Zonas</span>
              <Check size={14} className={styles.profilePreviewCheck} />
            </div>
            <div className={styles.profilePreviewZones}>
              {state.allCountry && (
                <span className={styles.profilePreviewZoneChip}>
                  Todo el país
                  <button
                    type="button"
                    className={styles.profilePreviewZoneRemove}
                    onClick={() => onRemoveZone('allCountry')}
                    aria-label="Eliminar Todo el país"
                  >
                    <X size={12} />
                  </button>
                </span>
              )}
              {state.workZones.map((zone: any, index: number) => (
                <span
                  key={`${zone.jurisdiccion?.id || zone.id}-${zone.city || index}`}
                  className={styles.profilePreviewZoneChip}
                >
                  {zone.jurisdiccion?.name || zone.name} - {zone.city || ''}
                  <button
                    type="button"
                    className={styles.profilePreviewZoneRemove}
                    onClick={() => onRemoveZone(zone.id || index)}
                    aria-label={`Eliminar ${zone.jurisdiccion?.name || zone.name}`}
                  >
                    <X size={12} />
                  </button>
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default ProfilePreview;
